import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Container, SectionHeader, PullQuote, CTAStrip } from "@/components/Sections";
import { PATHWAYS } from "@/lib/content";
import { ArrowRight } from "lucide-react";

export default function Pathways() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [location.hash]);

  return (
    <div data-testid="page-pathways" className="bg-[#F6F1E6]">
      <section className="pt-40 md:pt-52 pb-24">
        <Container>
          <p className="overline mb-6">Section 04 — Training Pathways</p>
          <h1
            data-testid="pathways-title"
            className="display-xxl text-5xl md:text-7xl lg:text-[112px] mb-10"
          >
            Pathways into the
            <br />
            <span className="italic text-[#3F5132]">restoration economy.</span>
          </h1>
          <p className="text-[#4F5A48] text-lg md:text-xl max-w-3xl leading-relaxed">
            Six emerging professions. Each pathway translates existing ranger
            and Traditional Owner capability into recognised skills, qualifications
            and careers in carbon, biodiversity, natural capital and native seed
            restoration.
          </p>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10">
        {PATHWAYS.map((p, idx) => (
          <article
            id={p.slug}
            key={p.slug}
            data-testid={`pathway-detail-${p.slug}`}
            className={`border-b border-[#1F2A1B]/10 py-24 md:py-32 ${
              idx % 2 === 1 ? "bg-[#EBE3D0]" : ""
            }`}
          >
            <Container>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                <div className="lg:col-span-4">
                  <div className="lg:sticky lg:top-32">
                    <span className="section-number">— Pathway {p.number}</span>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] mt-5 mb-8 text-[#1F2A1B]">
                      {p.title}
                    </h2>
                    <p className="text-[#4F5A48] text-lg leading-relaxed">
                      {p.summary}
                    </p>
                    <div className="aspect-[4/5] mt-10 img-hover relative">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover warm-tint"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-8 lg:pl-12 lg:border-l lg:border-[#1F2A1B]/10">
                  <p className="overline mb-6">Skills include</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-0 mb-16">
                    {p.skills.map((s, i) => (
                      <li
                        key={s}
                        className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10"
                      >
                        <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[#1F2A1B] text-base">{s}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="overline mb-6">Potential career pathways</p>
                  <div className="space-y-4">
                    {p.careers.map((c) => (
                      <div
                        key={c}
                        className="flex items-center justify-between border-b border-[#1F2A1B]/10 pb-4 group"
                      >
                        <span className="font-serif text-2xl md:text-3xl text-[#1F2A1B] group-hover:text-[#3F5132] transition-colors">
                          {c}
                        </span>
                        <ArrowRight
                          size={18}
                          className="text-[#8A8775] group-hover:text-[#C45A3C] transition-colors"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 mt-12">
                    {p.detailHref && (
                      <Link
                        to={p.detailHref}
                        data-testid={`pathway-${p.slug}-detail`}
                        className="btn-primary"
                      >
                        View full training page
                        <ArrowRight size={16} />
                      </Link>
                    )}
                    <Link
                      to="/contact"
                      data-testid={`pathway-${p.slug}-cta`}
                      className={p.detailHref ? "btn-ghost" : "btn-primary"}
                    >
                      Build this pathway with us
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            </Container>
          </article>
        ))}
      </section>

      <Container>
        <PullQuote testid="pathways-quote">
          Building skills. Building confidence. Building capability. Building
          pathways.
        </PullQuote>
      </Container>

      <CTAStrip
        heading="Design a pathway for your program."
        body="We work with ranger programs and Aboriginal corporations to translate existing capability into recognised environmental professions."
        label="Talk to us"
        testid="pathways-cta-strip"
      />
    </div>
  );
}
